import React, { useState, useEffect } from 'react';
import { tripService } from '../../services/tripService';
import { Trip } from '../../types';
import { useRouter, Link } from '../../router/Router';
import {
  Calendar,
  Clock,
  Bus,
  ShieldCheck,
  ArrowRight,
  Armchair,
} from 'lucide-react';

export const TripDetails: React.FC<{ tripId: string }> = ({ tripId }) => {
  const { navigate } = useRouter();
  const [trip, setTrip] = useState<Trip | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadTrip = async () => {
      setIsLoading(true);
      const data = await tripService.getTripById(tripId);
      setTrip(data);
      setIsLoading(false);
    };

    loadTrip();
  }, [tripId]);

  if (isLoading) {
    return (
      <div className="booking-page-container" dir="rtl">
        <div className="loading-state-card">
          <div className="loading-spinner"></div>
          <p>جاري تحميل تفاصيل الرحلة...</p>
        </div>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="booking-page-container" dir="rtl">
        <div className="empty-state-card">
          <h3>الرحلة المطلوبة غير موجودة</h3>
          <p>ربما تم إلغاء هذه الرحلة أو أن الرابط غير صحيح.</p>
          <Link to="/student/universities" className="btn-primary">
            العودة لاختيار الجامعة
          </Link>
        </div>
      </div>
    );
  }

  const seatsLeft =
    typeof trip.availableSeats === 'number'
      ? trip.availableSeats
      : trip.availableSeatsCount || 0;
  const isFull = seatsLeft <= 0;

  return (
    <div className="booking-page-container" dir="rtl">
      {/* Header */}
      <div className="booking-page-header">
        <div className="header-breadcrumbs">
          <Link to="/student" className="breadcrumb-link">
            لوحة الطالب
          </Link>
          <span className="breadcrumb-sep">/</span>
          <Link to={`/student/trips?universityId=${trip.universityId}`} className="breadcrumb-link">
            رحلات الجامعة
          </Link>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">تفاصيل الرحلة</span>
        </div>
        <h1 className="page-title">رحلة: {trip.universityName}</h1>
        <p className="page-subtitle">كود الرحلة: {trip.tripCode}</p>
      </div>

      <div className="trip-details-grid">
        {/* Main Details Card */}
        <div className="trip-details-card">
          <div className="details-header-row">
            <span className="details-badge">مباشر دون توقف</span>
            <div className="price-tag-lg">
              <span className="price-val">{trip.price}</span>
              <span className="price-curr">ج.م</span>
            </div>
          </div>

          <div className="details-info-sections">
            {/* Itinerary */}
            <div className="detail-section">
              <h4>خط سير الرحلة والمحطات</h4>
              <div className="itinerary-timeline">
                <div className="itinerary-node">
                  <div className="node-icon origin"></div>
                  <div>
                    <strong>نقطة الانطلاق (الإسكندرية):</strong>
                    <p>{trip.origin || trip.pickupLocation}</p>
                    <span className="time-badge">
                      <Clock size={12} /> {trip.departureTime} صباحًا
                    </span>
                  </div>
                </div>

                <div className="itinerary-node">
                  <div className="node-icon dest"></div>
                  <div>
                    <strong>محطة الوصول (الجامعة):</strong>
                    <p>{trip.destination || trip.destinationDetails || trip.universityName}</p>
                    <span className="time-badge">
                      <Clock size={12} /> {trip.expectedArrivalTime || trip.arrivalTime || '09:00'} صباحًا
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Vehicle & Date Details */}
            <div className="detail-section">
              <h4>المركبة وتاريخ السفر</h4>
              <div className="specs-grid">
                <div className="spec-item">
                  <span className="spec-label">تاريخ السفر:</span>
                  <span className="spec-val">
                    <Calendar size={14} /> {trip.date}
                  </span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">نوع السيارة:</span>
                  <span className="spec-val">
                    <Bus size={14} /> حافلة سياحية مجهزة (Coach Bus)
                  </span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">رقم لوحة السيارة:</span>
                  <span className="spec-val">{trip.vehiclePlate || 'س ق د 1842'}</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">سعة المقاعد:</span>
                  <span className="spec-val">50 راكبًا (مريحة ومكيفة)</span>
                </div>
                <div className="spec-item">
                  <span className="spec-label">المقاعد المتبقية:</span>
                  <span className={`spec-val ${seatsLeft <= 5 ? 'urgent' : ''}`}>
                    <Armchair size={14} /> {seatsLeft} مقعدًا متاحًا
                  </span>
                </div>
              </div>
            </div>

            {/* Guarantees */}
            <div className="guarantees-banner">
              <ShieldCheck size={20} className="shield-icon" />
              <div>
                <strong>ضمانات OTO STUDENTS للطلاب:</strong>
                <p>
                  يتم تعيين المقاعد آلياً بالترتيب وفقاً لأولوية الحجز، ومقعدك محجوز ومؤكد باسمك مسبقًا لضمان الراحة والانسيابية.
                </p>
              </div>
            </div>
          </div>

          <div className="details-action-box">
            <button
              className="btn-primary btn-proceed-seats"
              disabled={isFull}
              onClick={() => navigate(`/student/trips/${trip.id}/seats`)}
            >
              <span>{isFull ? 'الرحلة ممتلئة بالكامل' : 'تأكيد حجز مقعدك (تعيين تلقائي بالترتيب)'}</span>
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
